import { ReactNode } from "react";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import {
  Box,
  Stack,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import {
  ArrayInput,
  AutocompleteInput,
  BooleanInput,
  DateInput,
  DateTimeInput,
  NullableBooleanInput,
  NumberInput,
  ReferenceInput,
  SelectInput,
  SimpleFormIterator,
  TextInput,
  required,
  useRecordContext,
} from "react-admin";

import { Buyer, Supplier } from "../types";
import { CiclosGrid } from "./CiclosGrid";
import { DIAS_SEMANA } from "./diasSemana";
import { PersonErpLink, PersonReferenceShortcuts } from "./PersonErpLink";

const optionPersonsRenderer = (
  choice: {
    id?: number;
    nome_razao?: string;
    nome_fantasia?: string;
    cnpj?: string | number;
    cpf?: string | number;
  } | null,
) => {
  if (!choice) return "";
  return [
    choice.id,
    choice.nome_razao,
    choice.nome_fantasia ? `- ${choice.nome_fantasia}` : null,
    choice.cnpj ? `- ${choice.cnpj}` : null,
    choice.cpf ? `- ${choice.cpf}` : null,
  ]
    .filter(Boolean)
    .join(" ");
};

const optionBuyerRenderer = (choice?: Buyer | null) =>
  choice
    ? `${choice.nome_razao}${choice.caminho_canal ? ` ${choice.caminho_canal}` : ""}`
    : "";

const FormRow = ({ children }: { children: ReactNode }) => (
  <Stack
    direction={{ xs: "column", sm: "row" }}
    gap={1}
    sx={{ "& > *": { flex: 1, minWidth: 0 } }}
  >
    {children}
  </Stack>
);

export const SupplierInputs = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const record = useRecordContext<Supplier>();
  const isEdit = Boolean(record?.id);

  return (
    <Stack gap={3} direction={isMobile ? "column" : "row"}>
      <Stack gap={3} flex={1.8} minWidth={0}>
        <IdentificacaoInputs isEdit={isEdit} />
        <EmitentesInputs />
        <ParametrosInputs />
        <AnaliseInputs />
        <CompradorSituacaoInputs />
      </Stack>
      {isEdit ? (
        <Stack flex={1} minWidth={0} gap={1}>
          <Typography variant="h6">Ciclos</Typography>
          <CiclosGrid />
        </Stack>
      ) : null}
    </Stack>
  );
};

const IdentificacaoInputs = ({ isEdit }: { isEdit: boolean }) => (
  <Stack>
    <Typography variant="h6">Identificação</Typography>
    {isEdit ? (
      <Stack gap={0.5} mb={1}>
        <Typography variant="caption" color="text.secondary">
          Pessoa
        </Typography>
        <PersonErpLink />
      </Stack>
    ) : (
      <Stack gap={0.5} mb={1}>
        <ReferenceInput
          source="id_pessoa"
          reference="persons"
          perPage={50}
          filterToQuery={(pesquisa: string) => ({ pesquisa })}
        >
          <AutocompleteInput
            label="Pessoa"
            helperText="Pesquisar por ID, nome, CPF ou CNPJ"
            optionText={optionPersonsRenderer}
            translateChoice={false}
            validate={required()}
            shouldRenderSuggestions={(val: string) => val.trim().length > 2}
          />
        </ReferenceInput>
        <PersonReferenceShortcuts source="id_pessoa" />
      </Stack>
    )}
    <TextInput
      source="nome_fornec"
      label="Nome do fornecedor"
      helperText={false}
      fullWidth
    />
  </Stack>
);

const EmitentesInputs = () => (
  <ArrayInput source="emit" label="Emitentes">
    <SimpleFormIterator disableReordering fullWidth>
      <Stack gap={0.5}>
        <ReferenceInput
          source="id_pessoa"
          reference="persons"
          perPage={50}
          filterToQuery={(pesquisa: string) => ({ pesquisa })}
        >
          <AutocompleteInput
            label="Pessoa"
            helperText={false}
            optionText={optionPersonsRenderer}
            translateChoice={false}
            validate={required()}
            disableClearable
            shouldRenderSuggestions={(val: string) => val.trim().length > 2}
          />
        </ReferenceInput>
        <PersonReferenceShortcuts source="id_pessoa" />
      </Stack>
    </SimpleFormIterator>
  </ArrayInput>
);

const ParametrosInputs = () => (
  <Stack>
    <Typography variant="h6">Parâmetros</Typography>
    <FormRow>
      <NumberInput
        source="config_fornec.dias_tr"
        label="TR (fórmula)"
        helperText={false}
        readOnly
      />
      <DateInput
        source="config_fornec.ultima_oc"
        label="Última OC"
        helperText={false}
        readOnly
      />
      <TextInput
        source="config_fornec.prazo_pagamento"
        label="Prazo padrão"
        helperText={false}
      />
    </FormRow>
    <FormRow>
      <NumberInput
        source="config_fornec.ic_padrao"
        label="IC (índice de compra)"
        helperText={false}
      />
      <NumberInput
        source="config_fornec.intervalo_analise"
        label="IA (intervalo análise)"
        helperText={false}
      />
      <NumberInput
        source="config_fornec.pedido_minimo"
        label="Pedido mínimo"
        helperText={false}
      />
    </FormRow>
  </Stack>
);

const AnaliseInputs = () => (
  <Stack>
    <Typography variant="h6">Análise</Typography>
    <Box
      display={{ xs: "block", sm: "flex" }}
      gap={1}
      alignItems={{ sm: "flex-end" }}
      sx={{ "& > *": { minWidth: 0 } }}
    >
      <Box flex={1.5}>
        <DateTimeInput
          source="dthr_analise"
          label="Data da análise"
          helperText={false}
          fullWidth
        />
      </Box>
      <Box flex={1}>
        <SelectInput
          source="dia_semana_iso"
          label="Dia da semana"
          choices={DIAS_SEMANA}
          helperText={false}
          fullWidth
        />
      </Box>
      <Box flex={1.4}>
        <Stack direction="row" alignItems="center" gap={0.5}>
          <Typography variant="subtitle2">Semana</Typography>
          <Tooltip title="Número da semana no ano (1-52)" arrow>
            <HelpOutlineIcon fontSize="small" color="action" />
          </Tooltip>
        </Stack>
        <Stack direction="row" gap={1}>
          <BooleanInput
            source="config_fornec.semana_par"
            label="Par"
            helperText={false}
          />
          <BooleanInput
            source="config_fornec.semana_impar"
            label="Ímpar"
            helperText={false}
          />
        </Stack>
      </Box>
    </Box>
  </Stack>
);

const CompradorSituacaoInputs = () => (
  <Box
    display={{ xs: "block", sm: "flex" }}
    gap={1}
    sx={{ "& > *": { minWidth: 0 } }}
  >
    <Box flex={1.5}>
      <ReferenceInput
        source="id_comprador"
        reference="buyers"
        perPage={100}
        sort={{ field: "nome_razao", order: "ASC" }}
      >
        <SelectInput
          label="Comprador"
          optionText={optionBuyerRenderer}
          helperText={false}
          fullWidth
        />
      </ReferenceInput>
    </Box>
    <Box flex={1}>
      <NullableBooleanInput
        source="ativo"
        label="Situação"
        falseLabel="Inativo"
        trueLabel="Ativo"
        helperText={false}
        fullWidth
      />
    </Box>
    <Box flex={1.4}>
      <NullableBooleanInput
        source="consumo"
        label="Tipo de NF"
        falseLabel="Revenda"
        trueLabel="Consumo"
        helperText={false}
        fullWidth
      />
    </Box>
  </Box>
);
